import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  inject,
  Input,
  input,
  signal
} from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SideBarMenuItemGroup } from 'src/app/models/side-bar-menu-item-group';
import { ExpandableMenuItemComponent, MenuItemComponent, MenuItemGroupComponent, MenuItemLabelDirective } from '@bioreferencehealth/ui-kit';
import { AnalyticsClickDirective } from 'src/modules/analytics/directives/analytics-click.directive';
import { IsActiveMatchOptions } from '@angular/router';

@Component({
  selector: 'app-side-bar-navigation',
  imports: [ExpandableMenuItemComponent, MenuItemComponent, MenuItemGroupComponent, MenuItemLabelDirective, AnalyticsClickDirective],
  templateUrl: './side-bar-navigation.component.html',
  styleUrl: './side-bar-navigation.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
@UntilDestroy()
export class SideBarNavigationComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly breakpointObserver = inject(BreakpointObserver);

  public readonly menuItemGroupOptions: IsActiveMatchOptions = {
    paths: 'subset',
    queryParams: 'subset',
    fragment: 'ignored',
    matrixParams: 'ignored'
  };

  @Input() public menuItemGroups: SideBarMenuItemGroup[] | null = null;
  public readonly isCollapsed = input(false);
  public readonly isSmallScreen = signal(false);

  protected expandedItems: Record<string, boolean> = {};
  private expandedItemOrder: string[] = [];

  constructor() {
    this.initializeBreakpointObserver();
  }

  private initializeBreakpointObserver(): void {
    this.breakpointObserver
      .observe('(max-height: 66rem)')
      .pipe(untilDestroyed(this))
      .subscribe(result => {
        this.isSmallScreen.set(result.matches);
      });
  }

  @HostListener('window:resize')
  public onWindowResize(): void {
    this.collapseExpandedItemsToFit();
  }

  protected getExpandableItemKey(groupIndex: number, itemIndex: number): string {
    return `${groupIndex}-${itemIndex}`;
  }

  protected isItemExpanded(itemKey: string): boolean {
    return this.expandedItems[itemKey] ?? false;
  }

  protected onExpandableItemChange(itemKey: string, expanded: boolean): void {
    this.expandedItems = {
      ...this.expandedItems,
      [itemKey]: expanded
    };

    if (expanded) {
      // On small screens, collapse all other items to save space
      if (this.isSmallScreen()) {
        const itemsToCollapse = this.expandedItemOrder.filter(key => key !== itemKey);
        for (const keyToCollapse of itemsToCollapse) {
          this.expandedItems = {
            ...this.expandedItems,
            [keyToCollapse]: false
          };
        }
        this.expandedItemOrder = [itemKey];
        this.cdr.markForCheck();
      } else {
        this.expandedItemOrder = [...this.expandedItemOrder.filter(key => key !== itemKey), itemKey];
        queueMicrotask(() => this.collapseExpandedItemsToFit(itemKey));
      }
      return;
    }

    this.expandedItemOrder = this.expandedItemOrder.filter(key => key !== itemKey);
  }

  private collapseExpandedItemsToFit(skipCollapseKey?: string): void {
    if (!this.hasOverflow()) {
      return;
    }

    const collapsibleItems = this.expandedItemOrder.filter(itemKey => itemKey !== skipCollapseKey);

    for (const itemKey of collapsibleItems) {
      if (!this.expandedItems[itemKey]) {
        continue;
      }

      this.expandedItems = {
        ...this.expandedItems,
        [itemKey]: false
      };
      this.expandedItemOrder = this.expandedItemOrder.filter(key => key !== itemKey);
      this.cdr.markForCheck();

      if (!this.hasOverflow()) {
        return;
      }
    }
  }

  private hasOverflow(): boolean {
    const hostElement = this.elementRef.nativeElement;
    return hostElement.scrollHeight > hostElement.clientHeight;
  }
}
